import { Block, Grid, ProductColor } from '@aloft/toolkit-sui';

import { useEffect, useMemo, useRef } from 'react';

import { ColumnSection } from '../../../components/Analysis/ColumnComponents/ColumnSection.tsx';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../../redux/features/referenceBarUISlice.ts';
import {
  selectArvFilteredCompSequence,
  selectIsArvOrder,
  selectIsInspectifyOrder,
  selectOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import {
  selectActiveDepreciateSeparatelyID,
  selectAivArvActiveTab,
  selectObjectiveEditMode,
} from '../../../redux/features/selectors/uiSelectors.ts';
import {
  setActiveDepreciateSeparatelyID,
  setObjectiveEditMode,
} from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { DepreciateSeparatelyModal } from '../ColumnSections/DepreciateSeparately/DepreciateSeparatelyModal.tsx';
import { Depreciation } from '../ColumnSections/Depreciation.tsx';
import { EffectiveAgeSlider } from '../ColumnSections/EffectiveAgeSlider/EffectiveAgeSlider.tsx';
import { QualitySlider } from '../ColumnSections/QualitySlider.tsx';
import { EDIT_MODE_OFF } from '../consts.ts';
import { ObjectiveEditModal } from '../modals/objectiveEdit/ObjectiveEditModal.tsx';
import { ViewMode } from '../types.ts';
import { useHeaderByID } from './HeaderByID.tsx';
import { useImprovementsByID } from './ImprovementsByID.tsx';
import { useSitesByID } from './SitesByID.tsx';

export const AnalysisGrid = ({ viewMode }: { viewMode: ViewMode }) => {
  const opinionOfSiteValueRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const { hasArvOption } = useAppSelector(selectOrder);
  const activeTab = useAppSelector(selectAivArvActiveTab);
  const filteredCompSequence = useAppSelector(selectArvFilteredCompSequence);
  const objectiveEditMode = useAppSelector(selectObjectiveEditMode);
  const activeDepreciateSeparatelyID = useAppSelector(selectActiveDepreciateSeparatelyID);
  const includeArv = useAppSelector(selectIsArvOrder);
  const arvTab = useAppSelector(selectAivArvActiveTab);
  const inspectifyOrder = useAppSelector(selectIsInspectifyOrder);

  let backgroundColor: ProductColor = 'surface6';
  if (hasArvOption) {
    if (activeTab === 'aiv') backgroundColor = 'aivLight';
    else if (activeTab === 'arv') backgroundColor = 'arvLight';
  }

  const handleDepreciateSeparatelyModeToggle = useMemo(
    () => (id: string) => {
      dispatch(setActiveDepreciateSeparatelyID(id));
    },
    [dispatch],
  );

  const HeaderByID = useHeaderByID(
    (mode) => dispatch(setObjectiveEditMode(mode)),
    objectiveEditMode,
    includeArv ? arvTab : undefined,
    inspectifyOrder,
  );
  const SitesByID = useSitesByID(viewMode, opinionOfSiteValueRef);
  const ImprovementsByID = useImprovementsByID(viewMode);

  useEffect(() => {
    if (opinionOfSiteValueRef.current) {
      opinionOfSiteValueRef.current.focus({ preventScroll: true });

      dispatch(setSelectedDataSheet('site-analysis'));
      dispatch(setReferenceViewOpen(true));
    }
  }, [dispatch]);

  return (
    <Block overflow="auto" paddingLeft="10px" backgroundColor={backgroundColor}>
      <Grid
        className="analysis-grid"
        gridTemplateColumns={`repeat(${filteredCompSequence.length + 1}, 1fr)`}
        maxWidth="500px"
      >
        <ColumnSection
          className="analysis-grid-property-header"
          compSequence={filteredCompSequence}
          contentFill
          renderForId={HeaderByID}
        />

        <ColumnSection
          className="improvements"
          compSequence={filteredCompSequence}
          renderForId={ImprovementsByID}
          aivArvTab={includeArv ? arvTab : undefined}
        />

        <ColumnSection
          className="site"
          compSequence={filteredCompSequence}
          renderForId={SitesByID}
        />
        <ColumnSection
          className="quality-slider"
          compSequence={filteredCompSequence}
          renderForId={QualitySlider}
          backgroundColor="surface2"
          aivArvTab={includeArv ? arvTab : undefined}
        />
        <ColumnSection
          className="effective-age-slider"
          compSequence={filteredCompSequence}
          renderForId={EffectiveAgeSlider}
          backgroundColor="surface2"
          noTopBorder
          aivArvTab={includeArv ? arvTab : undefined}
        />
        <ColumnSection
          className="depreciation"
          compSequence={filteredCompSequence}
          renderForId={Depreciation}
          backgroundColor="surface2"
          noTopBorder
        />

        {objectiveEditMode !== EDIT_MODE_OFF && (
          <ObjectiveEditModal
            isModalOpen={objectiveEditMode !== EDIT_MODE_OFF}
            activeId={objectiveEditMode}
            onModalClose={() => dispatch(setObjectiveEditMode(objectiveEditMode))}
            setActiveId={(id) => dispatch(setObjectiveEditMode(id))}
            compSequence={filteredCompSequence}
          />
        )}

        {activeDepreciateSeparatelyID !== EDIT_MODE_OFF && (
          <DepreciateSeparatelyModal
            isModalOpen={activeDepreciateSeparatelyID !== EDIT_MODE_OFF}
            onModalClose={() => handleDepreciateSeparatelyModeToggle(EDIT_MODE_OFF)}
            compSequence={filteredCompSequence}
          />
        )}
      </Grid>
    </Block>
  );
};
